import { Container } from '..';
import Image from 'next/image';
import { FoodScoreComponent } from './FoodScoreComponent';
import { FoodDetailDataType } from 'utils/foodGlucoseDbHelper';
import { getSpikeIndicatorIcon } from './FoodListElement';
import { FoodSuggestions } from './FoodSuggestions';
import { FoodSpikeType, MealType, TimeToMealMap } from './types';
import styles from './FoodDetailComponent.module.css';

const cx = (...args: Array<string | false | undefined | null>) =>
  args.filter(Boolean).join(' ');

const LightBentoBox = ({
  className,
  ...rest
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={cx(styles.lightBentoBox, className)} {...rest} />
);

const ProgressBar = ({
  className,
  value,
  max,
  color,
}: {
  className?: string;
  value: number;
  max: number;
  color?: 'red' | 'green';
}) => {
  const progress = (value / max) * 100;

  const getProgressColor = () => {
    if (color === 'red') {
      return '#E52A1E';
    } else if (color === 'green') {
      return '#05FF00';
    } else if (progress < 40) {
      return '#E52A1E';
    } else if (progress < 70) {
      return '#FD9400';
    } else {
      return '#05FF00';
    }
  };

  const getTextColor = () => {
    if (progress < 40) {
      return '#ffffff';
    } else {
      return '#000000';
    }
  };

  return (
    <div className={cx(styles.progressBar, className)}>
      <div
        className={styles.progress}
        style={{ background: getProgressColor(), width: `${progress}%` }}
      >
        <span style={{ color: getTextColor() }}>{progress.toFixed(0)}%</span>
      </div>
    </div>
  );
};

export const FoodDetailComponent = ({
  className,
  data,
  suggestions,
}: {
  className?: string;
  data: FoodDetailDataType;
  suggestions: {
    name: string;
    food_id: number;
    url_friendly_name: string;
    average_score: number;
  }[];
}) => {
  return (
    <section className={cx(styles.root, className)}>
      <Container>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1>{data.name}</h1>
            <p className={styles.foodTime}>
              {data.meal_timing_to_show_at ? (
                <Image
                  src={
                    TimeToMealMap[data.meal_timing_to_show_at as MealType].image
                  }
                  alt="food-time"
                  height={16}
                  width={16}
                />
              ) : null}
              {TimeToMealMap[data.meal_timing_to_show_at as MealType]?.title}
            </p>
          </div>
          <div className={styles.visualisationContainer}>
            <LightBentoBox className="half">
              <div className="">
                <h2>
                  <span className={styles.lg}>
                    {Number(data.average_peak_glucose_levels).toFixed()}
                    {getSpikeIndicatorIcon(
                      data.spike_indicator as FoodSpikeType,
                      {},
                    )}
                  </span>
                  <span className={styles.sm}> mg/dL</span>
                </h2>
                <p className={styles.caption}>avg. peak value</p>
              </div>

              <p>{data.spike_subtitle}</p>
            </LightBentoBox>
            <LightBentoBox className="half">
              <FoodScoreComponent
                value={Math.round(Number(data.average_score))}
              />
              <p>Avg. Food Score on Ultrahuman App</p>
            </LightBentoBox>
            <LightBentoBox className={styles.full} style={{ gap: '2.4rem' }}>
              <ProgressBar
                value={
                  Number(data.percentage_stable_response) >= 50
                    ? data.percentage_stable_response
                    : 100 - data.percentage_stable_response
                }
                max={100}
                color={data.percentage_stable_response >= 50 ? 'green' : 'red'}
              />
              <p>
                Ultrahuman Users got{' '}
                {Number(data.percentage_stable_response) >= 50 ? (
                  <>
                    a <b>STABLE</b>
                  </>
                ) : (
                  <>
                    an <b>UNSTABLE</b>
                  </>
                )}{' '}
                response
              </p>
            </LightBentoBox>
            {suggestions.length ? (
              <LightBentoBox className={styles.full} style={{ gap: '2.4rem' }}>
                <FoodSuggestions data={suggestions} />
              </LightBentoBox>
            ) : null}
          </div>
        </div>
      </Container>
    </section>
  );
};
